import React, { useEffect, useState } from 'react';
import { fetchUsuarios, CustomUser } from '../../../services/userService';
import { fetchClientes } from '../../../services/clienteService';
import { fetchPersonal } from '../../../services/personalService';
import {
    Mail, Phone, CreditCard, Briefcase,
    Calendar, Search, MoreHorizontal, Building2, ArrowUpRight,
    CheckCircle2, ChevronRight, AlertCircle, FileCheck, Eye
} from 'lucide-react';
import { Link } from 'react-router-dom';
import ModalVerDeclaracionJurada from '../../../components/Clients/ModalVerDeclaracionJurada';

// ─── Avatar reutilizable ───────────────────────────────────────────────────────
interface UserAvatarProps {
    firstName?: string;
    lastName?: string;
    fotoUrl?: string | null;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ firstName, lastName, fotoUrl }) => {
    const [imgError, setImgError] = useState(false);
    const showPhoto = fotoUrl && !imgError;

    return (
        <div className="relative">
            <div
                className="h-14 w-14 rounded-xl flex items-center justify-center text-xl font-bold text-white shadow-md transform group-hover:scale-105 transition-transform duration-300 overflow-hidden"
                style={{ backgroundColor: showPhoto ? 'transparent' : '#8f1f64' }}
            >
                {showPhoto ? (
                    <img
                        src={fotoUrl}
                        alt={`${firstName} ${lastName}`}
                        className="w-full h-full object-cover"
                        onError={() => setImgError(true)}
                    />
                ) : (
                    <span>{firstName?.[0]}{lastName?.[0]}</span>
                )}
            </div>
            {/* Indicador de estado (se posiciona sobre el avatar) */}
        </div>
    );
};
// ──────────────────────────────────────────────────────────────────────────────

const UsuariosPage = () => {
    const [usuarios, setUsuarios] = useState<CustomUser[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Mapa userId → foto_url
    const [fotoMap, setFotoMap] = useState<Record<number, string | null>>({});
    // Mapa userId → info declaracion jurada
    const [declaracionMap, setDeclaracionMap] = useState<Record<number, { firmada: boolean; fecha?: string | null; foto_url?: string | null }>>({});
    const [filtroDeclaracion, setFiltroDeclaracion] = useState<'todos' | 'firmada' | 'pendiente'>('todos');
    const [modalDeclaracionOpen, setModalDeclaracionOpen] = useState(false);
    const [clienteDeclaracionModal, setClienteDeclaracionModal] = useState<{
        nombre?: string;
        email?: string;
        telefono?: string | null;
        ci_nit?: string | null;
        tipo_cliente?: string;
        fecha_declaracion_jurada?: string | null;
        foto_declaracion_jurada_url?: string | null;
    } | null>(null);

    // --- PALETA DE COLORES COMARCA (Formal: 3 colores clave) ---
    const theme = {
        primaryAction: '#c1348b',
        corporate: '#8f1f64',
        blue: '#61257b',
    };

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                // Carga en paralelo: usuarios + clientes + personal
                const [resultado, clientes, personal] = await Promise.all([
                    fetchUsuarios(),
                    fetchClientes(),
                    fetchPersonal(),
                ]);

                setUsuarios(resultado);

                // Construir mapa userId → foto_url y declaracion jurada
                const fMap: Record<number, string | null> = {};
                const dMap: Record<number, { firmada: boolean; fecha?: string | null; foto_url?: string | null }> = {};

                clientes.forEach((c) => {
                    if (c.usuario?.id != null) {
                        fMap[c.usuario.id] = c.foto_url ?? null;
                        dMap[c.usuario.id] = {
                            firmada: Boolean(c.declaracion_jurada_firmada),
                            fecha: c.fecha_declaracion_jurada,
                            foto_url: c.foto_declaracion_jurada_url
                        };
                    }
                });
                personal.forEach((p) => {
                    if (p.usuario?.id != null && !(p.usuario.id in fMap)) {
                        fMap[p.usuario.id] = p.foto_url ?? null;
                    }
                });
                setFotoMap(fMap);
                setDeclaracionMap(dMap);
            } catch (error) {
                console.error("Error cargando datos:", error);
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, []);

    const filteredUsuarios = usuarios.filter(usuario => {
        const fullName = `${usuario.first_name || ''} ${usuario.last_name || ''}`.toLowerCase();
        const email = usuario.email?.toLowerCase() || '';
        const phone = usuario.telefono?.toLowerCase() || '';
        const ciNit = usuario.ci_nit?.toLowerCase() || '';
        const cargo = usuario.cargo?.toLowerCase() || '';
        const term = searchTerm.toLowerCase();
        const matchSearch = fullName.includes(term) || email.includes(term) || phone.includes(term) || ciNit.includes(term) || cargo.includes(term);

        if (!matchSearch) return false;

        const decInfo = declaracionMap[usuario.id];
        if (filtroDeclaracion === 'firmada') {
            return decInfo?.firmada === true;
        }
        if (filtroDeclaracion === 'pendiente') {
            return usuario.perfil_asociado === 'cliente' && !decInfo?.firmada;
        }

        return true;
    }).sort((a, b) => b.id - a.id);

    const handleVerDeclaracion = (usuario: CustomUser) => {
        const decInfo = declaracionMap[usuario.id];
        setClienteDeclaracionModal({
            nombre: `${usuario.first_name || ''} ${usuario.last_name || ''}`.trim() || usuario.email,
            email: usuario.email,
            telefono: usuario.telefono,
            ci_nit: usuario.ci_nit,
            tipo_cliente: usuario.tipo_nivel_usuario?.nombre || 'Cliente',
            fecha_declaracion_jurada: decInfo?.fecha,
            foto_declaracion_jurada_url: decInfo?.foto_url,
        });
        setModalDeclaracionOpen(true);
    };

    const getTipoUsuario = (tipoNivel: { id: number; nombre: string } | null) => {
        return tipoNivel?.nombre || 'Miembro';
    };

    const getBadgeStyles = (tipoNivel: { id: number; nombre: string } | null) => {
        const base = "inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold tracking-wide border";
        if (!tipoNivel) return `${base} bg-gray-50 text-gray-600 border-gray-200`;

        switch (tipoNivel.id) {
            case 1: return `${base} bg-[#8f1f64]/5 text-[#8f1f64] border-[#8f1f64]/20`;
            case 2: return `${base} bg-[#61257b]/5 text-[#61257b] border-[#61257b]/20`;
            case 3: return `${base} bg-[#ffd97c]/20 text-[#ea8406] border-[#ffd97c]/50`;
            case 4: return `${base} bg-[#c1348b]/20 text-[#c1348b] border-[#ffd97c]/50`;
            case 5: return `${base} bg-[#ea8406]/20 text-[#ea8406] border-[#ffd97c]/50`;
            case 6: return `${base} bg-[#61257b]/20 text-[#61257b] border-[#8b3fa8]/50`;
            case 7: return `${base} bg-[#ea8406]/20 text-[#ea8406] border-[#8b3fa8]/50`;
            default: return `${base} bg-[#ea8406]/5 text-[#ea8406] border-[#ffd97c]/20`;
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'No disponible';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Fecha inválida';
            return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' });
        } catch {
            return 'Fecha inválida';
        }
    };

    return (
        <div className="min-h-screen bg-gray-50/50 dark:bg-transparent">
            <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl pt-10 pb-16">

                {/* --- HEADER --- */}
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-purple-300/70 mb-2 uppercase tracking-wider">
                            <Building2 className="h-4 w-4" style={{ color: theme.corporate }} />
                            <span>Administración & Kardex</span>
                        </div>
                        <h2 className="text-xl font-bold text-gray-800 dark:text-white">Directorio de Miembros</h2>
                        <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                            <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                            <ChevronRight className="h-3 w-3" />
                            <a href="/kardex-citas" className="text-[#8f1f64] dark:text-[#ffd97c] hover:text-[#c1348b]">Kardex</a>
                        </div>
                    </div>

                    <div className="bg-[#ffd97c]/20 px-4 py-2 rounded-full text-[#ea8406] font-medium flex items-center gap-2 border border-[#ffd97c]/40">
                        <span className="h-3 w-3 rounded-full bg-[#ea8406] animate-pulse"></span>
                        {usuarios.length} miembros registrados
                    </div>
                </div>

                {/* --- BARRA DE BÚSQUEDA Y FILTROS --- */}
                <div className="flex flex-col md:flex-row gap-4 mb-8">
                    <div className="relative flex-grow group">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400 group-focus-within:text-[#8f1f64] transition-colors" />
                        </div>
                        <input
                            type="text"
                            className="block w-full pl-10 pr-4 py-3 bg-white dark:bg-[#150a28] border border-gray-200 dark:border-purple-900/40 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#8f1f64]/20 focus:border-[#8f1f64] transition-all shadow-xs"
                            placeholder="Buscar por nombre, CI, email, teléfono..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    
                    {/* Filtros de Declaración Jurada */}
                    <div className="flex items-center bg-white dark:bg-[#150a28] p-1.5 rounded-xl border border-gray-200 dark:border-purple-900/40 shadow-xs gap-1">
                        <button
                            type="button"
                            onClick={() => setFiltroDeclaracion('todos')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                                filtroDeclaracion === 'todos'
                                    ? 'bg-[#8f1f64] text-white'
                                    : 'text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-purple-900/30'
                            }`}
                        >
                            Todos ({usuarios.length})
                        </button>
                        <button
                            type="button"
                            onClick={() => setFiltroDeclaracion('firmada')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 ${
                                filtroDeclaracion === 'firmada'
                                    ? 'bg-emerald-600 text-white'
                                    : 'text-emerald-700 hover:bg-emerald-50'
                            }`}
                        >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Dec. Firmada
                        </button>
                        <button
                            type="button"
                            onClick={() => setFiltroDeclaracion('pendiente')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 ${
                                filtroDeclaracion === 'pendiente'
                                    ? 'bg-amber-600 text-white'
                                    : 'text-amber-700 hover:bg-amber-50'
                            }`}
                        >
                            <AlertCircle className="h-3.5 w-3.5" />
                            Dec. Pendiente
                        </button>
                    </div>
                </div>

                <ModalVerDeclaracionJurada
                    isOpen={modalDeclaracionOpen}
                    onClose={() => setModalDeclaracionOpen(false)}
                    cliente={clienteDeclaracionModal}
                />

                {/* --- GRID DE USUARIOS --- */}
                {isLoading ? (
                    <div className="flex justify-center py-32">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: theme.primaryAction }}></div>
                    </div>
                ) : filteredUsuarios.length === 0 ? (
                    <div className="text-center py-24 bg-white rounded-2xl border border-dashed border-gray-300">
                        <div className="mx-auto h-16 w-16 rounded-full bg-gray-50 flex items-center justify-center mb-4">
                            <Search className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900">No se encontraron resultados</h3>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredUsuarios.map((usuario) => (
                            <div
                                key={usuario.id}
                                className="group relative bg-white dark:bg-[#150a28] rounded-xl border border-gray-200 dark:border-purple-900/40 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.35)] hover:border-[#8f1f64]/50 dark:hover:border-purple-600/60 transition-all duration-300 flex flex-col overflow-hidden"
                            >
                                <div className="absolute top-0 left-0 w-full h-1" style={{ backgroundColor: theme.corporate }}></div>

                                <div className="p-6 flex-grow pt-8">
                                    <div className="flex justify-between items-start mb-6">
                                        <div className="flex gap-4">
                                            {/* ── Avatar con foto o iniciales ── */}
                                            <div className="relative">
                                                <UserAvatar
                                                    firstName={usuario.first_name}
                                                    lastName={usuario.last_name}
                                                    fotoUrl={fotoMap[usuario.id]}
                                                />
                                                {/* Indicador de estado */}
                                                <div className={`absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white dark:border-[#150a28] flex items-center justify-center ${usuario.estado ? 'bg-green-500' : 'bg-gray-400'}`}>
                                                    {usuario.estado && <CheckCircle2 className="h-2.5 w-2.5 text-white" />}
                                                </div>
                                            </div>

                                            <div>
                                                <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                                                    {usuario.first_name} {usuario.last_name}
                                                </h3>
                                                <div className="mt-2 flex items-center gap-2">
                                                    <span className={getBadgeStyles(usuario.tipo_nivel_usuario)}>
                                                        {getTipoUsuario(usuario.tipo_nivel_usuario)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* --- DATOS FICHA TÉCNICA --- */}
                                    <div className="space-y-3 bg-gray-50/50 dark:bg-[#1c0e35] p-3.5 rounded-lg border border-gray-100 dark:border-purple-900/30">
                                        <div className="flex items-center justify-between text-sm">
                                            <div className="flex items-center gap-2 text-gray-500 dark:text-purple-300/80">
                                                <Mail className="h-3.5 w-3.5 text-[#8f1f64] dark:text-pink-400" />
                                                <span className="text-xs font-medium uppercase tracking-wide">Email</span>
                                            </div>
                                            <span className="font-medium text-gray-900 dark:text-slate-100 truncate max-w-[140px]" title={usuario.email || ''}>
                                                {usuario.email || '—'}
                                            </span>
                                        </div>

                                        <div className="flex items-center justify-between text-sm">
                                            <div className="flex items-center gap-2 text-gray-500 dark:text-purple-300/80">
                                                <Phone className="h-3.5 w-3.5 text-[#61257b] dark:text-purple-400" />
                                                <span className="text-xs font-medium uppercase tracking-wide">Teléfono</span>
                                            </div>
                                            <span className="font-medium text-gray-900 dark:text-slate-100 truncate">
                                                {usuario.telefono || '—'}
                                            </span>
                                        </div>

                                        <div className="flex items-center justify-between text-sm">
                                            {usuario.perfil_asociado === 'personal' ? (
                                                <>
                                                    <div className="flex items-center gap-2 text-gray-500 dark:text-purple-300/80">
                                                        <Briefcase className="h-3.5 w-3.5" />
                                                        <span className="text-xs font-medium uppercase tracking-wide">Cargo</span>
                                                    </div>
                                                    <span className="font-medium text-gray-900 dark:text-slate-100 truncate max-w-[140px]">
                                                        {usuario.cargo || '—'}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="flex items-center gap-2 text-gray-500 dark:text-purple-300/80">
                                                        <CreditCard className="h-3.5 w-3.5 text-[#c1348b] dark:text-pink-400" />
                                                        <span className="text-xs font-medium uppercase tracking-wide">CI/NIT</span>
                                                    </div>
                                                    <span className="font-medium text-gray-700 dark:text-purple-200 font-mono text-xs bg-white dark:bg-[#120822] px-1.5 py-0.5 rounded border border-gray-200 dark:border-purple-900/50">
                                                        {usuario.ci_nit || '—'}
                                                    </span>
                                                </>
                                            )}
                                        </div>

                                        {/* Estado de Declaración Jurada */}
                                        {usuario.perfil_asociado === 'cliente' && (
                                            <div className="flex items-center justify-between text-xs pt-2 border-t border-gray-200/60 dark:border-purple-900/40">
                                                <div className="flex items-center gap-1.5 text-gray-600 dark:text-purple-200/80">
                                                    <FileCheck className="h-3.5 w-3.5 text-[#8f1f64] dark:text-pink-400" />
                                                    <span className="font-bold text-[11px]">Dec. Jurada:</span>
                                                </div>
                                                {declaracionMap[usuario.id]?.firmada ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleVerDeclaracion(usuario)}
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors shadow-2xs group"
                                                        title="Ver foto de la firma"
                                                    >
                                                        <CheckCircle2 className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                                                        Firmada
                                                        {declaracionMap[usuario.id]?.foto_url && (
                                                            <Eye className="h-3 w-3 ml-0.5 text-emerald-700 dark:text-emerald-300 opacity-70 group-hover:opacity-100" />
                                                        )}
                                                    </button>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800/40">
                                                        <AlertCircle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                                                        Pendiente
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="px-6 py-4 border-t border-gray-100 dark:border-purple-900/30 bg-white dark:bg-[#150a28] rounded-b-xl flex items-center justify-between transition-colors hover:bg-gray-50/50 dark:hover:bg-purple-900/20">
                                    <div className="text-xs text-gray-400 dark:text-slate-400 flex flex-col">
                                        <span className="flex items-center gap-1">
                                            <Calendar className="h-3 w-3" />
                                            Registro
                                        </span>
                                        <span className="font-medium text-gray-600 dark:text-slate-200">{formatDate((usuario as any).fecha_creacion)}</span>
                                    </div>
                                    <Link
                                        to={`/kardex-citas/detalle/${usuario.id}`}
                                        className="px-3 py-1.5 rounded-lg text-xs font-bold text-[#8f1f64] dark:text-[#ea8406] hover:text-white hover:bg-[#8f1f64] dark:hover:bg-[#ea8406] dark:hover:text-black flex items-center gap-1 transition-all border border-[#8f1f64]/20 dark:border-[#ea8406]/30"
                                    >
                                        Ver Kardex
                                        <ArrowUpRight className="h-3.5 w-3.5" />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UsuariosPage;